import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Package, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import partsService from '../../services/parts';
import type { Part, PartCategory, PartFormData } from '../../types/parts';
import { PART_CATEGORIES } from '../../types/parts';
import { toNum } from '@/utils/numeric';

export default function PartsCatalog() {
  const navigate = useNavigate();
  const { hasAccess: canWrite } = useModuleLicense('vehicles');

  const [query, setQuery]         = useState('');
  const [category, setCategory]   = useState<PartCategory | ''>('');
  const [oemOnly, setOemOnly]     = useState<'' | 'oem' | 'aftermarket'>('');
  const [parts, setParts]         = useState<Part[]>([]);
  const [loading, setLoading]     = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing]     = useState<Part | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const res = await partsService.search(query, {
        category: category || undefined,
        is_oem: oemOnly === '' ? undefined : oemOnly === 'oem',
      }, 1, 50);
      setParts(res.parts);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [category, oemOnly]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    refresh();
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <PageHeader
        title="Parts Catalogue"
        subtitle="Global filters, oils, belts and consumables"
        actionButtons={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => navigate('/equipment')}>Back</Button>
            {canWrite && (
              <Button size="sm" onClick={() => { setEditing(null); setShowCreate(true); }}>
                <Plus className="h-4 w-4 mr-1" /> New Part
              </Button>
            )}
          </div>
        }
      />

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <Card>
          <CardContent className="pt-4 space-y-3">
            <form onSubmit={handleSearch} className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder="Search by part number, brand or description…"
                  className="w-full pl-9 pr-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
              <Button type="submit" size="sm">Search</Button>
            </form>

            <div className="flex gap-2">
              <select
                value={category}
                onChange={e => setCategory(e.target.value as PartCategory | '')}
                className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs"
              >
                <option value="">All categories</option>
                {PART_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <select
                value={oemOnly}
                onChange={e => setOemOnly(e.target.value as '' | 'oem' | 'aftermarket')}
                className="border border-neutral-200 rounded-lg px-2 py-1.5 text-xs"
              >
                <option value="">OEM + aftermarket</option>
                <option value="oem">OEM only</option>
                <option value="aftermarket">Aftermarket only</option>
              </select>
            </div>
          </CardContent>
        </Card>

        {(showCreate || editing) && canWrite && (
          <PartForm
            initial={editing}
            onClose={() => { setShowCreate(false); setEditing(null); }}
            onSaved={() => { setShowCreate(false); setEditing(null); refresh(); }}
          />
        )}

        {loading ? (
          <p className="text-sm text-neutral-500 text-center py-6">Loading…</p>
        ) : parts.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-10 w-10 text-neutral-200 mx-auto mb-3" />
            <p className="text-neutral-500">No parts match your search.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {parts.map(p => (
              <button
                key={p.id}
                onClick={() => canWrite && setEditing(p)}
                className="w-full bg-white border border-neutral-100 rounded-xl px-4 py-3 text-left hover:shadow-md transition-all"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-neutral-800">{p.brand}</p>
                      <span className="font-mono text-xs text-neutral-500">{p.part_number}</span>
                      {p.is_oem && <span className="text-[10px] text-green-700 bg-green-100 px-1.5 rounded">OEM</span>}
                    </div>
                    <p className="text-xs text-neutral-500 mt-0.5">
                      {PART_CATEGORIES.find(c => c.value === p.category)?.label ?? p.category}
                      {p.description ? ` · ${p.description}` : ''}
                    </p>
                  </div>
                  {p.typical_price != null && (
                    <p className="text-sm font-medium text-neutral-700 flex-shrink-0">${Number(p.typical_price).toFixed(2)}</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Create/edit form ───────────────────────────────────────────────────────
function PartForm({ initial, onClose, onSaved }: { initial: Part | null; onClose: () => void; onSaved: () => void }) {
  const [data, setData] = useState<PartFormData>({
    category:    initial?.category    ?? 'oil_filter',
    part_number: initial?.part_number ?? '',
    brand:       initial?.brand       ?? '',
    is_oem:      initial?.is_oem      ?? false,
    oem_make:    initial?.oem_make    ?? '',
    description: initial?.description ?? '',
    unit:        initial?.unit        ?? 'each',
    typical_price: toNum(initial?.typical_price),
    source_url:  initial?.source_url  ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  async function handleSave() {
    if (!data.brand.trim() || !data.part_number.trim()) { setError('Brand and part number are required.'); return; }
    setSaving(true);
    setError('');
    try {
      if (initial) {
        await partsService.update(initial.id, data);
      } else {
        await partsService.create(data);
      }
      onSaved();
    } catch {
      setError('Failed to save part.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardContent className="pt-4 space-y-3">
        <div className="flex items-center justify-between">
          <p className="font-semibold text-neutral-800">{initial ? 'Edit part' : 'New part'}</p>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600"><X className="h-4 w-4" /></button>
        </div>
        {error && <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Brand *</label>
            <input value={data.brand} onChange={e => setData({ ...data, brand: e.target.value })}
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Part number *</label>
            <input value={data.part_number} onChange={e => setData({ ...data, part_number: e.target.value })}
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm font-mono" />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Category</label>
            <select value={data.category}
              onChange={e => setData({ ...data, category: e.target.value as PartCategory })}
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm">
              {PART_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-xs">
              <input type="checkbox" checked={!!data.is_oem} onChange={e => setData({ ...data, is_oem: e.target.checked })} />
              OEM
            </label>
            <input
              value={data.oem_make ?? ''}
              onChange={e => setData({ ...data, oem_make: e.target.value })}
              placeholder="OEM make"
              className="flex-1 border border-neutral-200 rounded-lg px-2 py-1.5 text-sm"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-neutral-500 mb-1">Description</label>
          <textarea rows={2} value={data.description ?? ''}
            onChange={e => setData({ ...data, description: e.target.value })}
            className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm resize-none" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Unit</label>
            <input value={data.unit ?? ''} onChange={e => setData({ ...data, unit: e.target.value })}
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Typical $</label>
            <input type="number" step="0.01" min="0" value={data.typical_price ?? ''}
              onChange={e => setData({ ...data, typical_price: e.target.value ? parseFloat(e.target.value) : null })}
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" />
          </div>
          <div>
            <label className="block text-xs text-neutral-500 mb-1">Source URL</label>
            <input value={data.source_url ?? ''}
              onChange={e => setData({ ...data, source_url: e.target.value })}
              placeholder="https://…"
              className="w-full border border-neutral-200 rounded-lg px-2 py-1.5 text-sm" />
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
          <Button isLoading={saving} onClick={handleSave} className="flex-1">Save</Button>
        </div>
      </CardContent>
    </Card>
  );
}
